import { useEffect, useState } from 'react';
import type {
  SyntheticProbe,
  FleetSloMetric,
  AnomalyEvent,
  SyntheticProbeType,
} from '@hospasense/contracts';
import {
  fetchSyntheticProbes,
  fetchFleetSlos,
  fetchAnomalyEvents,
  createSyntheticProbe,
  resolveAnomalyEvent,
} from '../lib/queries-reliability.js';
import { useAuth } from '../lib/auth.js';
import {
  Button,
  Card,
  CardBody,
  PageHeader,
  Badge,
  StatusPill,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  Modal,
  Input,
  Select,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../components/ui/index.js';

export function ReliabilityPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('reliability.manage');

  const [probes, setProbes] = useState<SyntheticProbe[]>([]);
  const [slos, setSlos] = useState<FleetSloMetric[]>([]);
  const [anomalies, setAnomalies] = useState<AnomalyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isAddProbeOpen, setIsAddProbeOpen] = useState(false);
  const [newProbeName, setNewProbeName] = useState('');
  const [newProbeType, setNewProbeType] = useState<SyntheticProbeType>('http_health');
  const [newProbeTargetUrl, setNewProbeTargetUrl] = useState(
    'https://api.hospasense.internal/healthz',
  );
  const [newProbeInterval, setNewProbeInterval] = useState(30);
  const [newProbeTimeout, setNewProbeTimeout] = useState(5000);
  const [submittingProbe, setSubmittingProbe] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [probesData, slosData, anomaliesData] = await Promise.all([
        fetchSyntheticProbes(),
        fetchFleetSlos(),
        fetchAnomalyEvents(),
      ]);
      setProbes(probesData);
      setSlos(slosData);
      setAnomalies(anomaliesData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, []);

  const handleCreateProbe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingProbe(true);
    try {
      await createSyntheticProbe({
        name: newProbeName,
        probeType: newProbeType,
        targetUrl: newProbeTargetUrl,
        intervalSeconds: newProbeInterval,
        timeoutMs: newProbeTimeout,
      });
      setIsAddProbeOpen(false);
      setNewProbeName('');
      await loadAll();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmittingProbe(false);
    }
  };

  const handleResolveAnomaly = async (id: string) => {
    try {
      await resolveAnomalyEvent(id);
      await loadAll();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * With no SLOs recorded there is no uptime to report. The previous fallback
   * printed '99.982' — a precise, confident, entirely invented number that
   * appeared whenever measurement was MISSING, which is exactly when a
   * reliability screen must not reassure anybody. Absent data reads as absent.
   */
  const hasUptimeData = slos.length > 0;
  const avgUptime = hasUptimeData
    ? (slos.reduce((acc, m) => acc + m.currentPercentage, 0) / slos.length).toFixed(3)
    : null;

  return (
    <div className="space-y-8" data-testid="reliability-page">
      <PageHeader
        title="Site Reliability Engineering & SLOs"
        subtitle="30-day rolling availability, synthetic probe topologies, and automated anomaly triage."
        actions={
          canManage && (
            <Button
              variant="primary"
              data-testid="add-probe-button"
              onClick={() => setIsAddProbeOpen(true)}
            >
              Add Synthetic Probe
            </Button>
          )
        }
      />

      {error && (
        <ErrorState
          description={error}
          onRetry={() => {
            void loadAll();
          }}
        />
      )}

      {/* SRE Overview Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Rolling 30-Day Fleet Uptime
            </span>
            {hasUptimeData ? (
              <>
                <p className="mt-1 text-2xl font-bold text-success tabular-nums">{avgUptime}%</p>
                <span className="text-xs text-text-muted">Target SLA: 99.950%</span>
              </>
            ) : (
              <>
                <p
                  className="mt-1 text-2xl font-bold text-text-muted tabular-nums"
                  data-testid="uptime-no-data"
                >
                  No data
                </p>
                <span className="text-xs text-text-muted">
                  No SLO measurements recorded. Target SLA: 99.950%
                </span>
              </>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Active Synthetic Probes
            </span>
            <p className="mt-1 text-2xl font-bold text-text-primary tabular-nums">
              {probes.length}
            </p>
            <span className="text-xs text-text-muted">Probing every 30s</span>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <span className="text-xs font-semibold uppercase tracking-wider text-text-muted">
              Active Anomalies / Breaches
            </span>
            <p className="mt-1 text-2xl font-bold text-warning tabular-nums">
              {anomalies.filter((a) => a.status === 'open').length}
            </p>
            <span className="text-xs text-text-muted">Requires operator triage</span>
          </CardBody>
        </Card>
      </div>

      {/* 30-Day SLOs Table */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-text-primary">
          Fleet Service Level Objectives (30-Day Window)
        </h2>
        {loading ? (
          <LoadingState label="Loading SLO metrics..." />
        ) : (
          <TableContainer>
            <Table density="compact">
              <THead>
                <TR>
                  <TH>SLO Name</TH>
                  <TH>Description</TH>
                  <TH>Target</TH>
                  <TH>Current Level</TH>
                  <TH>Error Budget Remaining</TH>
                  <TH align="right">Status</TH>
                </TR>
              </THead>
              <TBody>
                {slos.map((m) => (
                  <TR key={m.id}>
                    <TD className="font-semibold text-text-primary">{m.name}</TD>
                    <TD className="text-xs text-text-muted">{m.description}</TD>
                    <TD className="text-xs text-text-muted">{m.targetPercentage.toFixed(2)}%</TD>
                    <TD className="font-semibold text-text-primary tabular-nums">
                      {m.currentPercentage.toFixed(2)}%
                    </TD>
                    <TD className="tabular-nums font-mono text-xs">
                      {m.errorBudgetRemainingPercent.toFixed(1)}%
                    </TD>
                    <TD align="right">
                      <StatusPill
                        tone={
                          m.status === 'meeting_slo'
                            ? 'success'
                            : m.status === 'at_risk'
                              ? 'warning'
                              : 'error'
                        }
                        label={
                          m.status === 'meeting_slo'
                            ? 'Meeting SLO'
                            : m.status === 'at_risk'
                              ? 'At Risk'
                              : 'Breached'
                        }
                      />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableContainer>
        )}
      </div>

      {/* Active Anomalies & Triage */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-text-primary">
          Reliability Anomalies & Alert Triage
        </h2>
        {anomalies.length === 0 ? (
          <EmptyState
            title="All systems within nominal operating tolerances"
            description="No active telemetry anomalies or synthetic probe failure cascades detected."
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {anomalies.map((anom) => (
              <Card key={anom.id}>
                <CardBody>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-mono text-xs font-semibold text-text-muted">
                        {anom.installationId}
                      </span>
                      <h3 className="text-base font-bold text-text-primary">{anom.title}</h3>
                    </div>
                    <Badge tone={anom.severity === 'critical' ? 'error' : 'warning'}>
                      {anom.severity}
                    </Badge>
                  </div>

                  <p className="mt-2 text-xs text-text-secondary">{anom.description}</p>

                  <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs">
                    <span className="text-text-muted">
                      Detected: {new Date(anom.detectedAt).toLocaleTimeString()}
                    </span>
                    {anom.status === 'open' && canManage ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        data-testid="resolve-anomaly-btn"
                        onClick={() => handleResolveAnomaly(anom.id)}
                      >
                        Resolve Anomaly
                      </Button>
                    ) : (
                      <Badge tone="neutral">Resolved</Badge>
                    )}
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Synthetic Probes Configuration */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-text-primary">Synthetic Availability Probes</h2>
        <TableContainer>
          <Table density="compact">
            <THead>
              <TR>
                <TH>Probe Name</TH>
                <TH>Target Endpoint</TH>
                <TH>Type</TH>
                <TH>Interval</TH>
                <TH>Timeout</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {probes.map((p) => (
                <TR key={p.id}>
                  <TD className="font-semibold text-text-primary">{p.name}</TD>
                  <TD className="font-mono text-xs text-text-muted">{p.targetUrl ?? '—'}</TD>
                  <TD>
                    <Badge tone="neutral">{p.probeType}</Badge>
                  </TD>
                  <TD className="text-xs">{p.intervalSeconds}s</TD>
                  <TD className="text-xs">{p.timeoutMs}ms</TD>
                  <TD>
                    <StatusPill
                      tone={p.status === 'active' ? 'success' : 'neutral'}
                      label={p.status}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      </div>

      {/* Add Probe Modal */}
      <Modal
        open={isAddProbeOpen}
        title="Add Synthetic Availability Probe"
        onClose={() => setIsAddProbeOpen(false)}
      >
        <form onSubmit={handleCreateProbe} className="space-y-3" data-testid="add-probe-form">
          <Input
            label="Probe Name"
            placeholder="e.g. Gateway Ping E2E"
            value={newProbeName}
            onChange={(e) => setNewProbeName(e.target.value)}
            required
          />
          <Select
            label="Probe Type"
            value={newProbeType}
            onChange={(e) => setNewProbeType(e.target.value as SyntheticProbeType)}
          >
            <option value="http_health">HTTP Health Check</option>
            <option value="db_ping">Database Ping</option>
            <option value="queue_latency">Queue Latency</option>
            <option value="backup_staleness">Backup Staleness</option>
            <option value="tls_cert">TLS Certificate Expiry</option>
          </Select>
          <Input
            label="Target URL"
            value={newProbeTargetUrl}
            onChange={(e) => setNewProbeTargetUrl(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Interval (Seconds)"
              type="number"
              value={String(newProbeInterval)}
              onChange={(e) => setNewProbeInterval(parseInt(e.target.value, 10))}
              required
            />
            <Input
              label="Timeout (ms)"
              type="number"
              value={String(newProbeTimeout)}
              onChange={(e) => setNewProbeTimeout(parseInt(e.target.value, 10))}
              required
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setIsAddProbeOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              loading={submittingProbe}
              data-testid="save-probe-btn"
            >
              Save Probe
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
